import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  OutlinedInput,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useEffect, useMemo, useState } from 'react';
import TableGroupObject from './Table';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import useObjectManagement from 'src/services/useObjectManagement';
import {
  reducerUpdateGroupObject,
  reducerUpdateObjectListByObjectType,
  reducerUpdateObjectManagementGroupSelectedById,
  reducerUpdateSelectedGroups,
  reducerUpdateSelectedEcommerceGroups
} from 'src/features/objectManagement';
import { IGroupObject, IObject } from 'src/models/objectManagement';
import { useLocation } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import ModalManageObjectByGroup from '../ModalManageObjectByGroup';
import DownloadIcon from '@mui/icons-material/Download';
import useDownload from 'src/services/useDownload';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAlert } from 'src/hooks/useAlert';
import useDebounceCallback from 'src/hooks/useDebounceCallback';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import ProductTourTooltip from '../ProductTourTooltip';
import { OBJECT_MANAGEMENT_GET_GROUP_LIST } from 'src/services/graphql/objectManagement/query';
import useEcommerce from 'src/services/useEcommerce';
import { ECOMMERCE_SOURCES } from 'src/constants';
import { TEcommerce } from '../EcommerceObjectPreview';
import { TExportType } from 'src/models/download';
import { IEcommerceProductHierarchyItem } from 'src/models/ecommerce';
import ModalCommentingToProduct from '../ModalCommentingToProduct';
import { t } from 'i18next';

const GroupObject = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isOverview = pathname.split('/')[1] === 'overview';
  const isEcommerce = pathname.split('/')[1] === 'ecommerce';
  const page = pathname.split('/')[2];
  const dispatch = useAppDispatch();
  const { downloadCsv: downloadMetrics } = useDownload();
  const {
    groupObject,
    objectList,
    selectedGroups,
    selectedEcommerceGroups,
    ecommerceObjectList
  } = useAppSelector((state) => state.objectManagement);
  const projectId = useAppSelector((state) => state.users.project.id);
  const timezone = useAppSelector((state) => state.users.project.timezone);
  const { objectManagementAddGroup, resObjectManagementAddGroup } =
    useObjectManagement();
  const [open, setOpen] = useState(false);
  const [groupName, setGroupName] = useState<string>('');
  const [editingGroup, setEditingGroup] = useState<boolean>(false);
  const [anchorElGroups, setAnchorElGroups] = useState<HTMLElement | null>(
    null
  );

  const {
    isOnTour,
    currentTour: { stepNumber, menu, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const { selectedOriginalToPost } = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const isCurrentlyOnTourStep10 =
    isOnTour &&
    stepNumber === 10 &&
    menu === 'social-media' &&
    submenu === 'overview';

  // when this state is empty, all group is selected
  const [selectedGroupsLocal, setSelectedGroupsLocal] = useState<
    IGroupObject[]
  >(isEcommerce ? selectedEcommerceGroups : selectedGroups);

  //commenting to product
  const [openCommentingToProduct, setOpenCommentingToProduct] =
    useState<boolean>(false);
  const [
    selectedCommentingToProductsLocal,
    setSelectedCommentingToProductsLocal
  ] = useState<IEcommerceProductHierarchyItem[]>(selectedOriginalToPost);

  const { handleClickAlert } = useAlert();
  const autoRefreshInterval = useAppSelector(
    (state) => state.autoRefreshMetric.selectedAutoRefreshInterval
  );

  const handleClose = () => {
    setAnchorElGroups(null);
    setOpen(false);
    handleFilter('');
  };

  const handleClickRow = (group: IGroupObject) => {
    const sourceData = isEcommerce
      ? ecommerceGroupData?.objectManagement_getObjectGroupList ?? []
      : groupObject.data;

    const isAllGroupClicked = group.id === 'all';
    const isGroupSelected = isAllGroupClicked
      ? selectedGroupsLocal.length === sourceData.length
      : selectedGroupsLocal.some((g) => g.id === group.id);

    if (isAllGroupClicked) {
      if (isGroupSelected) {
        // Unselect all - clear the selection
        setSelectedGroupsLocal([]);
      } else {
        // Select all - set to sourceData only
        setSelectedGroupsLocal(sourceData);
      }
      return;
    }

    // Handle individual group selection
    if (isGroupSelected) {
      // Remove the group from selection
      setSelectedGroupsLocal((prev) => prev.filter((g) => g.id !== group.id));
    } else {
      // Add the group to selection
      setSelectedGroupsLocal((prev) => [...prev, group]);
    }
  };

  const handleApply = () => {
    const sourceData = isEcommerce
      ? ecommerceGroupData?.objectManagement_getObjectGroupList ?? []
      : groupObject.data;

    const isAllGroupSelected = selectedGroupsLocal.length === sourceData.length;

    if (isAllGroupSelected) {
      const selectedGroupObjectList = selectedGroupsLocal
        .map((group) => group.objectList)
        .flat();
      const selectedGroupObjectIdList = selectedGroupObjectList.map(
        (obj) => obj.id
      );

      if (isEcommerce) {
        dispatch(reducerUpdateSelectedEcommerceGroups(selectedGroupsLocal));
      } else {
        dispatch(reducerUpdateSelectedGroups(selectedGroupsLocal));
      }
      dispatch(reducerUpdateObjectListByObjectType(selectedGroupObjectList));
      dispatch(
        reducerUpdateAnalyticArgumentObjectIdList(selectedGroupObjectIdList)
      );
      dispatch(reducerUpdateObjectManagementGroupSelectedById('all'));
      handleClose();
      return;
    }

    // no group selected
    if (selectedGroupsLocal.length === 0) {
      dispatch(reducerUpdateObjectManagementGroupSelectedById('_default'));
      if (isEcommerce) {
        dispatch(reducerUpdateSelectedEcommerceGroups([]));
      } else {
        dispatch(reducerUpdateSelectedGroups([]));
      }
      dispatch(reducerUpdateObjectListByObjectType(objectListByPage));
      dispatch(
        reducerUpdateAnalyticArgumentObjectIdList(
          objectListByPage.map((obj) => obj.id)
        )
      );
      handleClose();
      return;
    }

    // if multiple groups selected
    const selectedGroupObjects = selectedGroupsLocal
      .map((group) => group.objectList)
      .flat();

    dispatch(reducerUpdateObjectManagementGroupSelectedById(undefined));
    if (isEcommerce) {
      dispatch(reducerUpdateSelectedEcommerceGroups(selectedGroupsLocal));
    } else {
      dispatch(reducerUpdateSelectedGroups(selectedGroupsLocal));
    }
    dispatch(reducerUpdateObjectListByObjectType(selectedGroupObjects));
    dispatch(
      reducerUpdateAnalyticArgumentObjectIdList(
        selectedGroupObjects.map((obj) => obj.id)
      )
    );

    handleClose();
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (autoRefreshInterval > 0) {
      handleClickAlert({
        vertical: 'top',
        horizontal: 'center',
        severity: 'warning',
        message: 'You can manage objects only when auto refresh is off'
      });
      return;
    }
    setAnchorElGroups(event.currentTarget);
    setOpen(true);
  };

  const [openAddGroup, setOpenAddGroup] = React.useState(false);
  const handleClickOpenAddGroup = () => {
    setOpenAddGroup(true);
  };
  const handleCloseAddGroup = () => {
    setOpenAddGroup(false);
  };

  const handleCreateGroup = (e) => {
    e.preventDefault();
    objectManagementAddGroup({
      variables: {
        projectId,
        objectList: [],
        templateName: 'content',
        groupName,
        objectGroupType: isEcommerce ? 'ecommerce' : 'socmed'
      },
      refetchQueries: isEcommerce ? [OBJECT_MANAGEMENT_GET_GROUP_LIST] : []
    }).then((response) => {
      if (!isEcommerce) {
        const group: IGroupObject =
          response.data?.objectManagement_addObjectGroup;
        dispatch(
          reducerUpdateGroupObject({
            ...groupObject,
            data: [...groupObject.data, group]
          })
        );
        dispatch(reducerUpdateObjectManagementGroupSelectedById(group.id));
      }

      setOpenAddGroup(false);
      setEditingGroup(true);
      handleClose();
    });
  };

  const objectListByPage = useMemo((): Array<IObject> => {
    if (page === 'content' || page === 'community') {
      if (isEcommerce)
        return objectList.data.filter(
          (object) =>
            object.streamType === 'account' &&
            ECOMMERCE_SOURCES.includes(object.socialMedia as TEcommerce)
        );
      return objectList.data.filter(
        (object) => object.streamType === 'account'
      );
    }
    return isEcommerce ? ecommerceObjectList : objectList.data;
  }, [page, objectList]);

  const { useGetEcommerceGroups } = useEcommerce();

  const { data: ecommerceGroupData } = useGetEcommerceGroups({
    variables: {
      projectId,
      templateName: 'content',
      objectGroupType: 'ecommerce'
    }
  });

  const groupList = useMemo(() => {
    if (isEcommerce)
      return ecommerceGroupData?.objectManagement_getObjectGroupList ?? [];
    return groupObject.data;
  }, [isEcommerce, ecommerceGroupData, groupObject]);

  const { filteredList, handleFilter } =
    useFiltersByName<IGroupObject>(groupList);

  const { dateRangeFormatted, objectIdList } = useAppSelector(
    (state) => state.analyticArguments
  );
  const advanceSearchSocialMedia = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );
  const advanceSearchEcommerce = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const fillAdvanceSearchSocialMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchSocialMedia);
  }, [advanceSearchSocialMedia]);

  const fillAdvanceSearchEcommerce = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchEcommerce);
  }, [advanceSearchEcommerce]);

  const payloads = {
    columnName: 'engagementRate',
    projectId,
    timezone,
    start: dateRangeFormatted.start,
    end: dateRangeFormatted.end,
    granularity: {
      unit: 'day',
      value: 1
    },
    ...(isEcommerce ? fillAdvanceSearchEcommerce : fillAdvanceSearchSocialMedia)
  };

  const [anchorElDownload, setAnchorElDownload] =
    useState<HTMLButtonElement | null>(null);

  const handleClickDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDownload(event.currentTarget);
  };

  const handleCloseDownload = () => {
    setAnchorElDownload(null);
  };

  const handleDownload = (
    templateName: string,
    exportType: TExportType = 'csv'
  ) => {
    const tempTemplateName =
      templateName === 'social_media' || templateName === 'stream'
        ? 'streamPanel'
        : templateName;
    handleCloseDownload();

    const getObjectListPayload = () => {
      if (page !== 'content' && page !== 'community') return objectIdList;
      const dataList = objectList.data.filter((object) => {
        if (object.streamType !== 'account') return false;
        return objectIdList.find((id) => id === object.id);
      });
      return dataList.map((object) => {
        return object.id;
      });
    };

    const payload = {
      ...payloads,
      templateName: tempTemplateName,
      email: '',
      objectList: getObjectListPayload(),
      exportType: exportType
    };

    downloadMetrics({
      variables: payload
    });
  };

  const handleChangeSearch = useDebounceCallback((searchValue: string) => {
    handleFilter(searchValue);
  });

  const openDownload = Boolean(anchorElDownload);
  const id = openDownload ? 'simple-popover' : undefined;

  const isShowDownloadWordcloud =
    page !== 'social_media' &&
    page !== 'people_panel' &&
    page !== 'sna' &&
    !isEcommerce;

  const isStreamPanel = page === 'social_media';

  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    setSelectedGroupsLocal(
      isEcommerce ? selectedEcommerceGroups : selectedGroups
    );
  }, [selectedGroups, isEcommerce]);

  useEffect(() => {
    if (!isEcommerce) return;
    setSelectedCommentingToProductsLocal(selectedOriginalToPost);
  }, [isEcommerce, selectedOriginalToPost]);

  return (
    <Stack
      direction="row"
      flexWrap={'wrap'}
      alignItems={'center'}
      justifyContent={'space-between'}
      mt={2}
      gap={{ xs: 1, md: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} width="50%">
        <Button
          size="small"
          variant="outlined"
          onClick={!isOnTour ? handleOpen : undefined}
          sx={{
            color: 'text.primary',
            backgroundColor: 'white',
            borderRadius: theme.spacing(1),
            maxWidth: !matchesUpMd ? '310px' : isOverview ? '100%' : '400px',
            width: '100%',
            minHeight: '36px',
            borderColor: (theme) =>
              open ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
              backgroundColor: 'white'
            },
            '& .MuiButton-startIcon': { marginRight: 'auto' },
            '& .buttonText': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textAlign: 'left',
              flex: '1'
            },
            padding: '4px 8px'
          }}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0)',
                marginRight: '2px'
              }}
            />
          }
        >
          {selectedGroupsLocal.length <= 0 ? (
            <span className="buttonText">{t('all groups')}</span>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                // maxWidth: '320px',
                // maxWidth: '500px',
                width: '100%',
                overflow: 'scroll',
                scrollbarWidth: 'none'
              }}
            >
              {selectedGroupsLocal.map((group, index) => (
                <Chip
                  key={`${group}-${index}`}
                  variant="outlined"
                  sx={{
                    maxWidth:
                      selectedGroupsLocal.length > 1 ? '150px' : '300px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block'
                    }
                  }}
                  label={group.displayName}
                  size="small"
                />
              ))}
            </Box>
          )}
        </Button>
        {/* {isEcommerce ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOpenCommentingToProduct(true)}
            sx={{
              color: 'text.primary',
              backgroundColor: 'white',
              borderRadius: theme.spacing(1),
              maxWidth: !matchesUpMd ? '310px' : isOverview ? '100%' : '400px',
              width: '100%',
              minHeight: '36px',
              borderColor: (theme) =>
                openCommentingToProduct
                  ? theme.palette.primary.main
                  : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
                backgroundColor: 'white'
              },
              '& .MuiButton-startIcon': { marginRight: 'auto' },
              '& .buttonText': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                textAlign: 'left',
                flex: '1'
              },
              padding: '4px 8px'
            }}
          >
            {selectedCommentingToProductsLocal.length <= 0 ? (
              <span className="buttonText">All Commenting to Products</span>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 0.5,
                  // maxWidth: '320px',
                  // maxWidth: '500px',
                  width: '100%',
                  overflow: 'scroll',
                  scrollbarWidth: 'none'
                }}
              >
                {selectedCommentingToProductsLocal.map((product, index) => (
                  <Chip
                    key={`${product}-${index}`}
                    variant="outlined"
                    sx={{
                      maxWidth:
                        selectedCommentingToProductsLocal.length > 1
                          ? '150px'
                          : '300px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block'
                      }
                    }}
                    label={product.name}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Button>
        ) : null} */}
      </Stack>
      {openCommentingToProduct && (
        <ModalCommentingToProduct
          open={openCommentingToProduct}
          handleClose={() => setOpenCommentingToProduct(false)}
          selectedProducts={selectedCommentingToProductsLocal}
          onSelectedProductsChange={(
            products: IEcommerceProductHierarchyItem[]
          ) => {
            setSelectedCommentingToProductsLocal(products);
          }}
        />
      )}
      <Popover
        open={isCurrentlyOnTourStep10 || open}
        onClose={!isOnTour ? handleClose : undefined}
        anchorEl={anchorElGroups}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <ProductTourTooltip
          open={isCurrentlyOnTourStep10}
          placement="right"
          onClose={handleClose}
          maxWidth={400}
        >
          <Box sx={{ p: theme.spacing(2), maxWidth: '900px', width: '100%' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h4">{t('group')}</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={
                  !isOnTour
                    ? (event) => {
                      handleClickOpenAddGroup();
                      event.stopPropagation();
                      event.preventDefault();
                    }
                    : undefined
                }
              >
                {t('new group')} +
              </Button>
            </Stack>
            <Input
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              fullWidth
              placeholder={t('search source')}
              onChange={
                !isOnTour
                  ? (event) => handleChangeSearch(event.target.value)
                  : undefined
              }
              onKeyDown={
                !isOnTour
                  ? (e) => {
                    e.stopPropagation();
                  }
                  : undefined
              }
            />
            <Box sx={{ mt: theme.spacing(1) }}>
              <TableGroupObject
                groupObjects={[
                  {
                    displayName: t('all group'),
                    id: 'all',
                    name: 'All Group',
                    objectList: objectListByPage
                  },
                  ...filteredList
                ]}
                loading={groupObject.loading}
                initialSelectedGroups={selectedGroupsLocal}
                // groupSelectedById={groupSelectedById}
                onClick={!isOnTour ? handleClickRow : () => { }}
              />
            </Box>
            <Stack pt={2} alignItems="end">
              <Button
                variant="contained"
                onClick={!isOnTour ? () => handleApply() : undefined}
              >
                {t('apply')}
              </Button>
            </Stack>
          </Box>
        </ProductTourTooltip>
      </Popover>
      {page !== 'overview' &&
        page !== 'people_panel' &&
        page !== 'comparison' &&
        page !== 'clustering' &&
        !isOverview ? (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            minWidth: theme.spacing(6),
            height: 36,
            backgroundColor: '#FBFBFB',
            borderRadius: theme.spacing(1),
            flexBasis: '1'
          }}
          onClick={handleClickDownload}
        >
          {t('download report')}
        </Button>
      ) : (
        <></>
      )}

      <Dialog
        onClose={!isOnTour ? handleCloseAddGroup : undefined}
        open={openAddGroup}
        fullWidth
        maxWidth={'sm'}
      >
        <IconButton
          aria-label="close"
          onClick={!isOnTour ? handleCloseAddGroup : undefined}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            m: theme.spacing(3, 3, 1),
            textAlign: 'center'
          }}
        >
          <Typography variant="h3">{t('create new group')}</Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            m: theme.spacing(1, 3)
          }}
        >
          <form onSubmit={!isOnTour ? handleCreateGroup : undefined}>
            <FormControl fullWidth size="medium">
              <FormLabel>{t('group name')}</FormLabel>
              <OutlinedInput
                name="objectName"
                onChange={
                  !isOnTour ? (e) => setGroupName(e.target.value) : undefined
                }
              />
            </FormControl>
            <Box display="flex" justifyContent="center" mt={theme.spacing(4)}>
              <Button
                variant="contained"
                type={!isOnTour ? 'submit' : 'button'}
                size="large"
                sx={{
                  background: theme.palette.primary.dark
                }}
                disabled={resObjectManagementAddGroup.loading}
              >
                {t('create new')}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
      {/* render modal only when creating new group */}
      {editingGroup ? (
        <ModalManageObjectByGroup
          title="Group"
          isHidden={true}
          onCloseEdit={!isOnTour ? () => setEditingGroup(false) : () => { }}
          isEditing={editingGroup}
          showFilter={editingGroup}
        />
      ) : null}
      <Popover
        id={id}
        open={openDownload}
        anchorEl={anchorElDownload}
        onClose={!isOnTour ? handleCloseDownload : undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          <ListItemButton
            onClick={!isOnTour ? () => handleDownload(page) : undefined}
          >
            <ListItemText
              primary={`CSV - ${page === 'sna' ? 'SNA' : 'All Metrics'} `}
            />
          </ListItemButton>
          {/* {isStreamPanel ? (
            <ListItemButton
              onClick={!isOnTour ? () => handleDownload('sna') : undefined}
            >
              <ListItemText primary="CSV - SNA" />
            </ListItemButton>
          ) : null} */}
          {isShowDownloadWordcloud && (
            <ListItemButton
              onClick={
                !isOnTour ? () => handleDownload('wordCloudTalk') : undefined
              }
            >
              <ListItemText primary="CSV - Talk WorldCloud" />
            </ListItemButton>
          )}
          {(page === 'community' || page === 'conversation') && (
            <>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topInfluencer${page[0].toUpperCase() + page.substring(1)
                        }`
                      )
                    : undefined
                }
              >
                <ListItemText primary="CSV - Top Influencer" />
              </ListItemButton>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topTalker${page[0].toUpperCase() + page.substring(1)
                        }`
                      )
                    : undefined
                }
              >
                <ListItemText primary="CSV - Top Talker" />
              </ListItemButton>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topEngagement${page[0].toUpperCase() + page.substring(1)
                        }`
                      )
                    : undefined
                }
              >
                <ListItemText primary="CSV - Top Talker by engagement" />
              </ListItemButton>
            </>
          )}
          {/* XLSX SECTION */}
          {page !== 'dna' ? (
            <ListItemButton
              onClick={
                !isOnTour ? () => handleDownload(page, 'xlsx') : undefined
              }
            >
              <ListItemText
                primary={`XLSX - ${page === 'sna' ? 'SNA' : 'All Metrics'} `}
              />
            </ListItemButton>
          ) : null}
          {/* {isStreamPanel ? (
            <ListItemButton
              onClick={
                !isOnTour ? () => handleDownload('sna', 'xlsx') : undefined
              }
            >
              <ListItemText primary="XLSX - SNA" />
            </ListItemButton>
          ) : null} */}
          {isShowDownloadWordcloud && page !== 'dna' ? (
            <ListItemButton
              onClick={
                !isOnTour
                  ? () => handleDownload('wordCloudTalk', 'xlsx')
                  : undefined
              }
            >
              <ListItemText primary="XLSX - Talk WorldCloud" />
            </ListItemButton>
          ) : null}
          {(page === 'community' || page === 'conversation') && (
            <>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topInfluencer${page[0].toUpperCase() + page.substring(1)
                        }`,
                        'xlsx'
                      )
                    : undefined
                }
              >
                <ListItemText primary="XLSX - Top Influencer" />
              </ListItemButton>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topTalker${page[0].toUpperCase() + page.substring(1)
                        }`,
                        'xlsx'
                      )
                    : undefined
                }
              >
                <ListItemText primary="XLSX - Top Talker" />
              </ListItemButton>
              <ListItemButton
                onClick={
                  !isOnTour
                    ? () =>
                      handleDownload(
                        `topEngagement${page[0].toUpperCase() + page.substring(1)
                        }`,
                        'xlsx'
                      )
                    : undefined
                }
              >
                <ListItemText primary="XLSX - Top Talker by engagement" />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>
    </Stack>
  );
};

export default GroupObject;
